/**
 * /privacidad — Política de Privacidad de Lumfi.
 *
 * Adapted to Lumfi's actual data flows. Email addresses surfaced from
 * the admin config (/api/public/support-info) so they can be changed
 * without a redeploy. When unset, falls back to the generic
 * "hola@lumfi.app" placeholder + an in-page admin notice.
 */
import { useLang } from "../../contexts/LangContext";
import { useSupportInfo } from "../../hooks/useSupportInfo";
import { LegalPage, Section } from "./LegalPage";
import { tx } from "../../i18n";

const LAST_UPDATED = "5 de junio de 2026";
const FALLBACK_EMAIL = "hola@lumfi.app";

// Inline mailto helper — keeps the JSX terse while still rendering a
// clickable address.
function EmailLink({ email, fallback = FALLBACK_EMAIL }) {
  const addr = email || fallback;
  return <a href={`mailto:${addr}`} data-testid={`legal-email-${addr.replace(/[@.]/g, "-")}`}>{addr}</a>;
}

const buildCopy = ({ contactEmail, privacyEmail, securityEmail }) => ({
  es: {
    eyebrow: "Legal",
    title: "Política de Privacidad",
    intro: (
      <>
        En <strong>Lumfi</strong> (operado por <strong>Wintime Cash Pilot SAS</strong>) tomamos
        tu privacidad muy en serio. Esta política describe qué información recopilamos, cómo la
        usamos y qué derechos tienes sobre ella. Si tienes preguntas, escríbenos a{" "}
        <EmailLink email={privacyEmail} />.
      </>
    ),
    sections: [
      {
        id: "datos-recopilados",
        title: "1. Datos que recopilamos",
        body: (
          <>
            <p>
              Lumfi solo procesa lo que tu nos cuentas. <strong>No nos conectamos a tu banco</strong>,
              ni a tus tarjetas, ni a APIs financieras públicas. Los datos que ingresan al sistema son:
            </p>
            <ul>
              <li>
                <strong>Cuenta y perfil</strong>: nombre, correo electrónico, país, idioma, moneda
                principal, profesión declarada y zona horaria. Sirven para autenticarte y localizar tu
                experiencia.
              </li>
              <li>
                <strong>Movimientos financieros</strong>: cobros, pagos, deudas, inversiones, metas y
                cuentas de efectivo que tu cargas manualmente o vía nuestros asistentes (web, Telegram,
                WhatsApp).
              </li>
              <li>
                <strong>Conversaciones con LumiBot</strong>: el contenido de tus mensajes al asistente
                IA, junto con metadatos (timestamp, canal, idioma). Los usamos para entender tu
                solicitud y devolverte un movimiento estructurado.
              </li>
              <li>
                <strong>Datos técnicos</strong>: IP, user-agent, identificador anónimo de sesión,
                eventos de uso (qué pantalla viste, qué bot vinculaste). Nunca rastreamos comportamiento
                fuera de Lumfi.
              </li>
              <li>
                <strong>Datos de pago</strong>: si te suscribes a un plan pago, Mercado Pago procesa la
                tarjeta. Lumfi <strong>nunca</strong> almacena el PAN ni el CVV — solo recibimos un
                token y la confirmación de cobro.
              </li>
            </ul>
          </>
        ),
      },
      {
        id: "uso",
        title: "2. Cómo usamos tus datos",
        body: (
          <>
            <ul>
              <li>Calcular tu efectivo real, retiro semanal seguro y semáforo de riesgo.</li>
              <li>
                Pasar el texto de tu mensaje a un modelo de lenguaje (OpenAI GPT, Anthropic Claude o
                Google Gemini, según la función) para extraer la intención. Las llamadas pasan por la
                infraestructura de Emergent.
              </li>
              <li>Enviar notificaciones operativas: confirmaciones, alertas FX, recordatorios.</li>
              <li>
                Comunicaciones de marketing <strong>solo si las activas explícitamente</strong>. Puedes
                desactivarlas en <a href="/email-preferences">Preferencias de email</a>.
              </li>
              <li>
                Detectar abuso, fraude o uso indebido (ej. cuentas falsas creadas para hacer scraping
                del catálogo de planes).
              </li>
            </ul>
            <p>
              <strong>No</strong> usamos tus números financieros para entrenar modelos de IA propios ni
              de terceros. Las llamadas a los proveedores LLM se hacen con tu prompt y desaparecen del
              caché del proveedor en horas, según sus propias políticas (OpenAI 30 días, Anthropic 30
              días).
            </p>
          </>
        ),
      },
      {
        id: "compartir",
        title: "3. Con quién compartimos los datos",
        body: (
          <>
            <p>Solo con proveedores estrictamente necesarios para operar el servicio:</p>
            <ul>
              <li>
                <strong>Mercado Pago</strong>: cobros recurrentes y one-time. Recibe email, país,
                monto y el plan/pack que compras. Política:{" "}
                <a href="https://www.mercadopago.com.ar/ayuda/politica-de-privacidad_3990" target="_blank" rel="noreferrer">
                  mercadopago.com
                </a>.
              </li>
              <li>
                <strong>Twilio</strong>: envío y recepción de WhatsApp. Recibe tu número de teléfono y
                el cuerpo del mensaje únicamente cuando interactúas con el bot.
              </li>
              <li>
                <strong>Telegram</strong>: si vinculaste el bot, Telegram conoce tu username/chat_id.
                Nosotros solo guardamos un identificador, nunca tu lista de contactos.
              </li>
              <li>
                <strong>Resend</strong>: envío de correos transaccionales y marketing. Recibe tu
                email, idioma y el cuerpo del mensaje.
              </li>
              <li>
                <strong>Emergent / OpenAI / Anthropic / Google</strong>: procesamiento del lenguaje
                natural cuando charlas con LumiBot.
              </li>
              <li>
                <strong>MongoDB Atlas (AWS, región us-east-1)</strong>: hosting de la base de datos.
              </li>
            </ul>
            <p>
              No vendemos, alquilamos ni intercambiamos tus datos con terceros con fines publicitarios.
              <strong> Punto.</strong>
            </p>
          </>
        ),
      },
      {
        id: "retencion",
        title: "4. Cuánto tiempo conservamos los datos",
        body: (
          <>
            <ul>
              <li><strong>Cuenta activa</strong>: mientras la cuenta exista.</li>
              <li>
                <strong>Cuenta eliminada</strong>: 30 días en backups encriptados, luego se borran
                definitivamente. Las facturas que exigen las autoridades fiscales locales se conservan
                el plazo legal mínimo (5 años en la mayoría de países LATAM).
              </li>
              <li>
                <strong>Logs operativos</strong>: 90 días para soporte e investigación de errores.
              </li>
            </ul>
          </>
        ),
      },
      {
        id: "derechos",
        title: "5. Tus derechos",
        body: (
          <>
            <p>
              Sin importar dónde vivas, en Lumfi tienes derecho a:
            </p>
            <ul>
              <li><strong>Acceso</strong>: pedir una copia completa de tus datos.</li>
              <li><strong>Rectificación</strong>: corregir cualquier dato inexacto.</li>
              <li><strong>Eliminación</strong>: borrar tu cuenta y todo lo asociado (hay un botón en /perfil).</li>
              <li><strong>Portabilidad</strong>: exportar tus movimientos en CSV/JSON.</li>
              <li><strong>Oposición</strong>: pedir que dejemos de procesar tus datos para fines no esenciales.</li>
              <li><strong>Revocación de consentimiento</strong>: por ejemplo, desactivar marketing en /email-preferences.</li>
            </ul>
            <p>
              Si vives en la Unión Europea o el Reino Unido también te aplica el RGPD. En Brasil, la
              LGPD. En Argentina, la Ley 25.326. En Colombia, la Ley 1581 de 2012. Si crees que no
              cumplimos, podes denunciarnos ante la autoridad de protección de datos de tu país (en CO:
              SIC, en BR: ANPD, en AR: AAIP, en MX: INAI).
            </p>
          </>
        ),
      },
      {
        id: "seguridad",
        title: "6. Cómo protegemos los datos",
        body: (
          <>
            <ul>
              <li>TLS 1.3 en todas las conexiones (web, bots, API).</li>
              <li>Hash bcrypt para contraseñas, JWT con rotación de secreto.</li>
              <li>Base de datos cifrada at-rest en AWS.</li>
              <li>Acceso administrativo limitado a 2 personas con MFA obligatorio.</li>
              <li>Auditoría completa: cada operación administrativa queda en el log de auditoría.</li>
            </ul>
            <p>
              Si descubres una vulnerabilidad, escríbenos a{" "}
              <EmailLink email={securityEmail} /> y respondemos en
              menos de 48 horas. Tenemos un programa informal de reconocimiento a investigadores.
            </p>
          </>
        ),
      },
      {
        id: "cookies",
        title: "7. Cookies y almacenamiento local",
        body: (
          <>
            <p>
              Lumfi usa <code>localStorage</code> y un puñado de cookies estrictamente funcionales:
            </p>
            <ul>
              <li><code>lumfi.session</code> — token JWT de tu sesión.</li>
              <li><code>lumfi.lang</code> — idioma elegido (es/en/pt).</li>
              <li><code>lumfi.country</code> — país preferido para precios.</li>
              <li><code>lumfi.lead.visitor_id</code> — para que el chat del landing recuerde la conversación entre visitas.</li>
            </ul>
            <p>
              No usamos cookies de tracking publicitario (no hay Facebook Pixel, no hay Google Ads
              tag). Tenemos analítica liviana propia para entender qué funcionalidades se usan, sin
              perfiles individuales.
            </p>
          </>
        ),
      },
      {
        id: "menores",
        title: "8. Menores de edad",
        body: (
          <p>
            Lumfi no está dirigido a menores de 18 años. Si descubrimos que una cuenta fue creada por
            un menor sin autorización de su tutor, la suspendemos y borramos los datos en 7 días.
          </p>
        ),
      },
      {
        id: "cambios",
        title: "9. Cambios a esta política",
        body: (
          <p>
            Si actualizamos esta política te avisaremos por email <strong>al menos 15 días antes</strong> de
            que entre en vigor, salvo que sean ajustes editoriales sin impacto en tus derechos. La fecha
            de la última actualización está al inicio de este documento.
          </p>
        ),
      },
      {
        id: "contacto",
        title: "10. Cómo contactarnos",
        body: (
          <>
            <p>
              <strong>Wintime Cash Pilot SAS</strong> — NIT en trámite
              <br />Email general: <EmailLink email={contactEmail} />
              <br />Privacidad y datos: <EmailLink email={privacyEmail} />
              <br />Seguridad: <EmailLink email={securityEmail} />
              <br />WhatsApp comercial: el número configurado en{" "}
              <a href="/">la home</a> (Hablanos por WhatsApp).
            </p>
          </>
        ),
      },
    ],
  },
  en: {
    eyebrow: "Legal",
    title: "Privacy Policy",
    intro: (
      <>
        At <strong>Lumfi</strong> (operated by <strong>Wintime Cash Pilot SAS</strong>) we take your
        privacy seriously. This policy explains what data we collect, how we use it and what rights
        you have. Questions? <EmailLink email={privacyEmail} />.
      </>
    ),
    sections: [
      {
        id: "data-collected",
        title: "1. Data we collect",
        body: (
          <>
            <p>
              Lumfi only processes what you tell it. <strong>We do not connect to your bank</strong>,
              cards, or public financial APIs. Data flowing into our system:
            </p>
            <ul>
              <li><strong>Account & profile</strong>: name, email, country, language, primary currency, declared profession and timezone.</li>
              <li><strong>Financial movements</strong>: receivables, payments, debts, investments, goals and cash accounts you log manually or via our assistants (web, Telegram, WhatsApp).</li>
              <li><strong>LumiBot conversations</strong>: your messages plus metadata (timestamp, channel, language). Used to interpret your intent.</li>
              <li><strong>Technical data</strong>: IP, user-agent, anonymous session id, usage events. We never track behavior outside Lumfi.</li>
              <li><strong>Payment data</strong>: Mercado Pago processes your card. Lumfi <strong>never</strong> stores the PAN or CVV — only a token and the charge confirmation.</li>
            </ul>
          </>
        ),
      },
      {
        id: "usage",
        title: "2. How we use your data",
        body: (
          <>
            <ul>
              <li>Compute your real cash, safe weekly withdrawal and monthly risk light.</li>
              <li>Send your chat text to a large-language-model provider (OpenAI GPT, Anthropic Claude or Google Gemini) routed through Emergent&apos;s infrastructure to extract intent.</li>
              <li>Send operational notifications: confirmations, FX alerts, reminders.</li>
              <li>Marketing communications <strong>only if you opt in</strong>. Toggle them at <a href="/email-preferences">Email preferences</a>.</li>
              <li>Fraud & abuse detection.</li>
            </ul>
            <p>
              <strong>We do not</strong> use your financial figures to train our own or third-party AI
              models. LLM provider caches expire per their policies (OpenAI 30 days, Anthropic 30 days).
            </p>
          </>
        ),
      },
      {
        id: "sharing",
        title: "3. Who we share data with",
        body: (
          <>
            <p>Only with vendors strictly necessary to operate the service:</p>
            <ul>
              <li><strong>Mercado Pago</strong> — recurring + one-time charges.</li>
              <li><strong>Twilio</strong> — WhatsApp message routing.</li>
              <li><strong>Telegram</strong> — bot interactions.</li>
              <li><strong>Resend</strong> — transactional + marketing email.</li>
              <li><strong>Emergent / OpenAI / Anthropic / Google</strong> — natural language understanding.</li>
              <li><strong>MongoDB Atlas (AWS us-east-1)</strong> — database hosting.</li>
            </ul>
            <p>We do not sell, rent or trade your data for advertising. <strong>Period.</strong></p>
          </>
        ),
      },
      {
        id: "retention",
        title: "4. Data retention",
        body: (
          <ul>
            <li><strong>Active account</strong>: while it exists.</li>
            <li><strong>Deleted account</strong>: 30 days in encrypted backups, then permanently wiped. Invoices required by local tax authorities are kept for the legal minimum (5 years in most LATAM jurisdictions).</li>
            <li><strong>Operational logs</strong>: 90 days for support and incident investigation.</li>
          </ul>
        ),
      },
      {
        id: "rights",
        title: "5. Your rights",
        body: (
          <>
            <p>Regardless of jurisdiction, you have the right to:</p>
            <ul>
              <li><strong>Access</strong> — request a full copy of your data.</li>
              <li><strong>Correct</strong> — fix anything inaccurate.</li>
              <li><strong>Delete</strong> — wipe your account from /profile.</li>
              <li><strong>Port</strong> — export movements as CSV/JSON.</li>
              <li><strong>Object</strong> — stop non-essential processing.</li>
              <li><strong>Withdraw consent</strong> — e.g. disable marketing at /email-preferences.</li>
            </ul>
            <p>
              GDPR applies if you live in the EU/UK. LGPD in Brazil. Ley 25.326 in Argentina. Ley 1581
              in Colombia. You can complain to your local DPA (CO: SIC, BR: ANPD, AR: AAIP, MX: INAI).
            </p>
          </>
        ),
      },
      {
        id: "security",
        title: "6. How we protect data",
        body: (
          <>
            <ul>
              <li>TLS 1.3 everywhere.</li>
              <li>bcrypt password hashes, JWT with secret rotation.</li>
              <li>Encryption at rest on AWS.</li>
              <li>Admin access limited to 2 people with mandatory MFA.</li>
              <li>Full audit log for admin operations.</li>
            </ul>
            <p>Found a vulnerability? <EmailLink email={securityEmail} /> — we respond in &lt;48h.</p>
          </>
        ),
      },
      {
        id: "cookies",
        title: "7. Cookies & local storage",
        body: (
          <>
            <p>We use <code>localStorage</code> and a few strictly-functional cookies:</p>
            <ul>
              <li><code>lumfi.session</code> — your JWT.</li>
              <li><code>lumfi.lang</code> — language (es/en/pt).</li>
              <li><code>lumfi.country</code> — preferred pricing country.</li>
              <li><code>lumfi.lead.visitor_id</code> — landing chat thread continuity.</li>
            </ul>
            <p>No ad-tracking pixels. No Facebook Pixel, no Google Ads tag.</p>
          </>
        ),
      },
      {
        id: "minors",
        title: "8. Minors",
        body: (
          <p>Lumfi is not intended for users under 18. Accounts created by minors without guardian consent will be suspended and data deleted within 7 days.</p>
        ),
      },
      {
        id: "changes",
        title: "9. Changes to this policy",
        body: (
          <p>We&apos;ll email you <strong>at least 15 days before</strong> any material change. The last-updated date sits at the top of this page.</p>
        ),
      },
      {
        id: "contact",
        title: "10. How to reach us",
        body: (
          <p>
            <strong>Wintime Cash Pilot SAS</strong><br />
            General: <EmailLink email={contactEmail} /><br />
            Privacy & data: <EmailLink email={privacyEmail} /><br />
            Security: <EmailLink email={securityEmail} />
          </p>
        ),
      },
    ],
  },
  pt: null, // Reuses Spanish content in this MVP — Portuguese translation pending.
});

export default function Privacy() {
  const { lang } = useLang();
  const { data: info } = useSupportInfo();
  // Resolve emails: admin-configured legal contacts win; fall back to
  // the public support contact; final fallback is the hardcoded
  // placeholder + we render a banner asking admin to configure.
  const contactEmail  = info?.contact_email || null;
  const privacyEmail  = info?.legal_privacy_email  || contactEmail || null;
  const securityEmail = info?.legal_security_email || contactEmail || null;
  const COPY = buildCopy({ contactEmail, privacyEmail, securityEmail });

  // PT translation pending — fall back to ES with a soft notice.
  const content = COPY[lang] || COPY.es;
  const showPtFallback = lang === "pt" && !COPY.pt;
  const showAdminNotice = !contactEmail && !privacyEmail && !securityEmail;
  return (
    <LegalPage
      title={content.title}
      eyebrow={content.eyebrow}
      lastUpdated={LAST_UPDATED}
      dataTestid="page-legal-privacy"
    >
      {showAdminNotice && (
        <div
          data-testid="legal-no-contact-notice"
          className="mb-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm"
        >
          {lang === "en"
            ? "Heads up: no contact emails configured. Set them in /admin → Communications."
            : lang === "pt"
              ? "Atenção: nenhum email de contato configurado em /admin → Comunicações."
              : "Aviso: no hay emails de contacto configurados. Hacelo desde /admin → Comunicaciones."}
        </div>
      )}
      {showPtFallback && (
        <div
          data-testid="legal-pt-fallback-notice"
          className="mb-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm"
        >
          A tradução em português está em andamento — por enquanto, mostramos a versão em espanhol.
        </div>
      )}
      <p className="text-lg text-slate-700 leading-relaxed">{content.intro}</p>
      {/* iter-169zv · Fase 5 — Sticky TOC en mobile/desktop para saltos rápidos. */}
      {content.sections?.length > 1 && (
        <nav
          aria-label={tx(lang, "Table of contents", "Índice", "Índice")}
          data-testid="legal-toc"
          className="sticky top-2 z-10 -mx-2 my-6 px-2 py-3 bg-white/95 backdrop-blur border border-slate-200 rounded-2xl shadow-sm"
        >
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2 px-2">
            {tx(lang, "Contents", "Índice", "Índice")}
          </p>
          <ol className="flex flex-wrap gap-1.5 px-2">
            {content.sections.map((s, i) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  data-testid={`legal-toc-${s.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium text-slate-700 bg-slate-100 hover:bg-emerald-100 hover:text-emerald-800 transition-colors"
                >
                  <span className="text-slate-400 font-mono text-[10px]">{String(i + 1).padStart(2, "0")}</span>
                  <span className="truncate max-w-[16ch] sm:max-w-none">{s.title}</span>
                </a>
              </li>
            ))}
          </ol>
        </nav>
      )}
      {content.sections.map((s) => (
        <Section key={s.id} id={s.id} title={s.title}>
          {s.body}
        </Section>
      ))}
    </LegalPage>
  );
}
